import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import type { Request } from 'express';

import getMatchedRoute from '@rover/react-app/src/server/renderPage/getMatchedRoute';
import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiCountryConfigurationNavigationRetrieve,
  getApiCountryConfigurationNavigationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-configuration-navigation-retrieve';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiFrontendServiceTypesList,
  getApiFrontendServiceTypesListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http500Error } from '@rover/utilities/HttpError';
import { parseSeoLocationFromSegments, SeoLocationDescriptor } from '@rover/utilities/seo';
import { redirect } from '@rover/utilities/url.server';

import GroomingSeoLocationContainer from './containers/GroomingSeoLocationContainer/GroomingSeoLocationContainer';
import SeoLocationContainer from './containers/SeoLocationContainer/SeoLocationContainer';
import { handleSeoLocationQueryError } from './utils/handleSeoLocationQueryError';
import {
  getApiSearchSeoLocationPrefetchQueryOptions,
  getApiSearchSeoLocationReviewsListPrefetchQueryOptions,
  useApiSearchSeoLocationRetrieve,
} from './utils/useSeoLocationHooks';
import { SeoLocationEngineContainer } from './types';

type RouteParams = {
  seoLocationSlug: string;
  countryCode?: string;
  languageCode?: string;
};

const ENGINE_CONTAINERS: Record<EngineEnum, SeoLocationEngineContainer> = {
  [EngineEnum.template]: SeoLocationContainer,
  [EngineEnum.legacy]: SeoLocationContainer,
  [EngineEnum.grooming]: GroomingSeoLocationContainer,
};

const getEngineContainer = (engine: EngineEnum): SeoLocationEngineContainer => {
  const container = ENGINE_CONTAINERS[engine];
  if (container) return container;

  captureMessage('Unmapped SEO location engine', {
    tags: { pathname: getPath(), engine },
  });
  return SeoLocationContainer;
};

const BaseSeoLocationContainer: FC & {
  reactQueryPrefetch: (queryClient: QueryClient, req: Request) => Promise<any>;
} = () => {
  const {
    seoLocationSlug = '',
    countryCode: countryUrlPrefix,
    languageCode,
  } = useParams<RouteParams>();
  const seoLocationDescriptor = parseSeoLocationFromSegments(
    countryUrlPrefix,
    languageCode,
    seoLocationSlug
  );
  const {
    data: seoLocation,
    isLoading: isLoadingSeoLocation,
    isError: isErrorSeoLocation,
    error: errorSeoLocation,
  } = useApiSearchSeoLocationRetrieve(seoLocationDescriptor);

  if (isErrorSeoLocation) {
    // 410s should be handled during prefetch, but just in case, also handle them here
    if (errorSeoLocation) {
      handleSeoLocationQueryError(errorSeoLocation);
    }

    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: { pathname: getPath(), isErrorSeoLocation },
    });
    throw new Http500Error();
  }

  if (isLoadingSeoLocation) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage('SeoLocationPageContainer rendered with missing prefetches', {
        tags: { pathname: getPath(), isLoadingSeoLocation },
      });
    }

    return (
      <RoverLoading
        style={{ height: '100vh' }}
        data-testid="rover-loading"
        isLoading={isLoadingSeoLocation}
      />
    );
  }

  if (!seoLocation) throw new Http500Error();

  // NOTE: This should never happen as the dog training slugs evaluate first in the seoRouteResolver
  if (seoLocation.details.serviceTypeSlug === ServiceTypeSlugEnum['dog-training']) {
    throw new Http500Error();
  }

  const { engine } = seoLocation.details.configuration;
  const EngineContainer = getEngineContainer(engine);

  return (
    <EngineContainer seoLocation={seoLocation} seoLocationDescriptor={seoLocationDescriptor} />
  );
};

/**
 * The queries every engine needs, whichever template it renders. Runtime twin of
 * `useSeoLocationPageData`: a query added to one belongs in the other.
 */
const prefetchSharedQueries = (
  queryClient: QueryClient,
  seoLocationDescriptor: SeoLocationDescriptor
): Promise<unknown> =>
  Promise.all([
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceTypesListQueryKey(),
      queryFn: () => apiFrontendServiceTypesList(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryConfigurationNavigationRetrieveQueryKey(),
      queryFn: () => apiCountryConfigurationNavigationRetrieve(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
      queryFn: () => apiFrontendConfigurationRetrieve(),
    }),
    queryClient.prefetchQuery(
      getApiSearchSeoLocationReviewsListPrefetchQueryOptions(seoLocationDescriptor)
    ),
  ]);

BaseSeoLocationContainer.reactQueryPrefetch = async (queryClient: QueryClient, req: Request) => {
  const { pageRoute } = getMatchedRoute(req.path);
  // v6 `RouteMatch` flattens params onto the match itself — there's no nested
  // `.match` like there was in v5.
  const {
    seoLocationSlug,
    countryCode: countryUrlPrefix = 'us',
    languageCode,
  } = pageRoute?.params ?? {};
  const seoLocationDescriptor = parseSeoLocationFromSegments(
    countryUrlPrefix,
    languageCode,
    seoLocationSlug
  );

  let seoLocation: SeoLocation;
  try {
    seoLocation = await queryClient.fetchQuery(
      getApiSearchSeoLocationPrefetchQueryOptions(seoLocationDescriptor)
    );
  } catch (error) {
    return handleSeoLocationQueryError(error);
  }

  // redirect if the canonical url doesn't match the requested url.
  // This can happen with typos (`---` instead of `--`), or if the service type slug
  // is not translated (`dog-boarding` instead of `cuidadores-de-perros` for /es/).
  const url = new URL(seoLocation.details.url);
  if (req.path !== url.pathname) {
    url.search = req.originalUrl.split('?')[1] ?? '';
    redirect(url);
  }

  const EngineContainer = getEngineContainer(seoLocation.details.configuration.engine);

  return Promise.all([
    prefetchSharedQueries(queryClient, seoLocationDescriptor),
    EngineContainer.reactQueryPrefetch(queryClient, { seoLocation, seoLocationDescriptor }),
  ]);
};

export default BaseSeoLocationContainer;
